import React, { useState } from "react";
import {
	Alert,
	Box,
	Button,
	LinearProgress,
	Link,
	TextField,
	Typography,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setLogin, setManagerDetails } from "state";
import { clearDashboardFetchCache } from "services/dashboardFetch";
import { baseURL } from "services/API";
import { applyStaffLoginResponse, parseStaffApiError } from "./staffAuth";

const requestSchema = yup.object().shape({
	username: yup.string().required("Username is required"),
});

const resetSchema = yup.object().shape({
	otp_code: yup.string().required("OTP is required").min(4, "Enter the code you received"),
	password: yup.string().required("Password is required").min(8, "Use at least 8 characters"),
	confirm_password: yup
		.string()
		.required("Confirm your password")
		.oneOf([yup.ref("password")], "Passwords must match"),
});

const ForgotPasswordForm = ({ onBackToLogin }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [step, setStep] = useState(1);
	const [username, setUsername] = useState("");
	const [deliveryHint, setDeliveryHint] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [alert, setAlert] = useState({ show: false, severity: "error", message: "" });

	const showAlert = (message, severity = "error") => {
		setAlert({ show: true, severity, message });
	};

	const handleRequestOtp = async (values) => {
		setIsLoading(true);
		setAlert({ show: false, severity: "error", message: "" });
		try {
			const response = await fetch(`${baseURL}/accounts/staff/forgot-password`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ username: values.username.trim() }),
			});
			const body = await response.json();
			if (response.status === 201 || body.status === 201) {
				setUsername(values.username.trim());
				const delivery = body.data?.delivery;
				const msg = body.data?.message || "OTP sent.";
				setDeliveryHint(delivery ? `${msg} (${delivery})` : msg);
				setStep(2);
			} else {
				showAlert(parseStaffApiError(body, "Could not send OTP. Check your username."));
			}
		} catch {
			showAlert("Network error. Please try again.");
		} finally {
			setIsLoading(false);
		}
	};

	const handleResetPassword = async (values, { resetForm }) => {
		setIsLoading(true);
		setAlert({ show: false, severity: "error", message: "" });
		try {
			const response = await fetch(`${baseURL}/accounts/staff/reset-password`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					username,
					otp_code: values.otp_code.trim(),
					password: values.password,
					confirm_password: values.confirm_password,
				}),
			});
			const body = await response.json();
			const ok = applyStaffLoginResponse({
				responseBody: body,
				dispatch,
				navigate,
				setLogin,
				setManagerDetails,
				clearDashboardFetchCache,
				onDenied: () => {
					showAlert(
						"Access denied. This app is for Admin, Property Manager, Relationship Manager, or Broker only."
					);
				},
				onFailure: (message) => showAlert(message),
			});
			if (ok) resetForm();
		} catch {
			showAlert("Network error. Please try again.");
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Box>
			<Typography variant="h5" fontWeight={600} textAlign="center" sx={{ mb: 1 }}>
				Reset password
			</Typography>
			<Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2 }}>
				{step === 1
					? "Enter your staff username. We will send a one-time code to your phone."
					: `Enter the OTP sent for ${username}, then choose a new password.`}
			</Typography>

			{alert.show && (
				<Alert severity={alert.severity} sx={{ mb: 2, borderRadius: "1rem" }}>
					{alert.message}
				</Alert>
			)}
			{step === 2 && deliveryHint && (
				<Alert severity="info" sx={{ mb: 2, borderRadius: "1rem" }}>
					{deliveryHint}
				</Alert>
			)}

			{step === 1 ? (
				<Formik
					initialValues={{ username: "" }}
					validationSchema={requestSchema}
					onSubmit={handleRequestOtp}
				>
					{({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
						<form onSubmit={handleSubmit}>
							<TextField
								fullWidth
								margin="normal"
								label="Username"
								name="username"
								value={values.username}
								onChange={handleChange}
								onBlur={handleBlur}
								error={Boolean(touched.username && errors.username)}
								helperText={touched.username && errors.username}
								placeholder="e.g. laaaa or +256…"
							/>
							{isLoading ? (
								<LinearProgress color="warning" sx={{ mt: 2, borderRadius: "2rem" }} />
							) : (
								<Button
									fullWidth
									type="submit"
									sx={{
										mt: 2,
										p: "1rem",
										borderRadius: "2rem",
										backgroundColor: "#141b2d",
										color: "#FCB200",
										"&:hover": { color: "#141b2d" },
									}}
								>
									Send OTP
								</Button>
							)}
						</form>
					)}
				</Formik>
			) : (
				<Formik
					initialValues={{ otp_code: "", password: "", confirm_password: "" }}
					validationSchema={resetSchema}
					onSubmit={handleResetPassword}
				>
					{({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
						<form onSubmit={handleSubmit}>
							<TextField
								fullWidth
								margin="normal"
								label="OTP code"
								name="otp_code"
								value={values.otp_code}
								onChange={handleChange}
								onBlur={handleBlur}
								error={Boolean(touched.otp_code && errors.otp_code)}
								helperText={touched.otp_code && errors.otp_code}
								inputProps={{ inputMode: "numeric", autoComplete: "one-time-code" }}
							/>
							<TextField
								fullWidth
								margin="normal"
								label="New password"
								name="password"
								type="password"
								value={values.password}
								onChange={handleChange}
								onBlur={handleBlur}
								error={Boolean(touched.password && errors.password)}
								helperText={touched.password && errors.password}
								autoComplete="new-password"
							/>
							<TextField
								fullWidth
								margin="normal"
								label="Confirm password"
								name="confirm_password"
								type="password"
								value={values.confirm_password}
								onChange={handleChange}
								onBlur={handleBlur}
								error={Boolean(touched.confirm_password && errors.confirm_password)}
								helperText={touched.confirm_password && errors.confirm_password}
								autoComplete="new-password"
							/>
							{isLoading ? (
								<LinearProgress color="warning" sx={{ mt: 2, borderRadius: "2rem" }} />
							) : (
								<Button
									fullWidth
									type="submit"
									sx={{
										mt: 2,
										p: "1rem",
										borderRadius: "2rem",
										backgroundColor: "#141b2d",
										color: "#FCB200",
										"&:hover": { color: "#141b2d" },
									}}
								>
									Reset password & sign in
								</Button>
							)}
							<Button fullWidth sx={{ mt: 1 }} onClick={() => setStep(1)}>
								Resend OTP — back to username
							</Button>
						</form>
					)}
				</Formik>
			)}

			<Box textAlign="center" mt={2}>
				<Link component="button" type="button" variant="body2" onClick={onBackToLogin}>
					Back to sign in
				</Link>
			</Box>
		</Box>
	);
};

export default ForgotPasswordForm;
